import type { AddNewDataFormValues } from "@/modules/add-new-data/lib/schema";
import {
  resolveBirthdate,
  type AgeBasis,
  type AgeSource,
  type BirthdatePrecision,
  type ResolvedBirthdate,
} from "@/modules/add-new-data/lib/age";
import { getActiveWebAdapter } from "@/stores/settings-store";

export type RbimRecordSourceSystem = "Old RBIM" | "New RBIM";
export type RbimRecordImportMode = "Legacy Continuation" | "New Encoding";

export type RbimFamilyUnit = {
  id: string;
  householdId: string;
  familyUnitNo: string;
  familyName: string;
  familyHeadIndividualId?: string;
  familyHeadName?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
};

export type RbimResidentProfile = {
  residentId: string;
  householdId: string;
  submissionId: string;
  submittedAt: string;
  lineNumber: number;
  fullName: string;
  surname: string;
  firstName: string;
  middleName: string;
  sex: string;
  dateOfBirth: string;
  displayBirthdate: string;
  birthdatePrecision: BirthdatePrecision;
  ageYears: number | null;
  /** Current age from the authorized server profile for a report snapshot. */
  reportAge?: number | null;
  ageMonths: number | null;
  ageDisplay: string;
  ageBasis: AgeBasis;
  ageSource: AgeSource | null;
  ageAsOfDate: string | null;
  isAgeApproximate: boolean;
  ageGroup: string;
  relationshipToHouseholdHead: string;
  familyUnitId: string;
  familyUnitNo: string;
  familyName: string;
  relationshipToFamilyHead: string;
  isFamilyHead: boolean;
  civilStatus: string;
  educationLevel: string;
  schoolAttendance: string;
  occupation: string;
  income: string;
  sourceOfIncome: string;
  workStatus: string;
  migrationStatus: string;
  healthInsurance: string;
  disability: string;
  householdCode: string;
  barangay: string;
  cityMunicipality: string;
  province: string;
  sourceSystem: RbimRecordSourceSystem;
  importMode: RbimRecordImportMode;
  importStatus: "Clean" | "Imported with Warning" | "Needs Review";
  reviewWarnings: string[];
  legacyRawData?: Record<string, unknown>;
  importedAt?: string;
  cleanedData: Record<string, unknown>;
  rawSource?: Record<string, unknown>;
};

export type RbimHouseholdProfile = {
  householdId: string;
  submissionId: string;
  submittedAt: string;
  householdNumber: string;
  householdCode: string;
  province: string;
  cityMunicipality: string;
  barangay: string;
  purok: string;
  address: string;
  householdHeadName: string;
  respondentName: string;
  totalMembers: number;
  familyUnitCount: number;
  familyUnits: RbimFamilyUnit[];
  members: RbimResidentProfile[];
  sourceSystem: RbimRecordSourceSystem;
  importMode: RbimRecordImportMode;
  importStatus: "Clean" | "Imported with Warning" | "Needs Review";
  reviewWarnings: string[];
  legacyRawData?: Record<string, unknown>;
  importedAt?: string;
  cleanedData: Record<string, unknown>;
  rawSource?: Record<string, unknown>;
};

export type RbimSubmissionEditContext = {
  values: AddNewDataFormValues;
  selectedResidentName: string;
  householdCode: string;
  familyName: string;
  initialMemberCount: number;
};

export function resolveResidentBirthdate(resident: RbimResidentProfile): ResolvedBirthdate {
  const cleaned = resident.cleanedData ?? {};
  const raw = resident.rawSource ?? {};
  const value = (key: string) => cleaned[key] ?? raw[key];

  const resolved = resolveBirthdate({
    dateOfBirth: resident.dateOfBirth || String(value("dateOfBirth") ?? ""),
    birthdatePrecision: resident.birthdatePrecision || (value("birthdatePrecision") as BirthdatePrecision | undefined),
    birthdateOriginalValue: String(value("birthdateOriginalValue") ?? ""),
    birthYear: toNullableNumber(value("birthYear")),
    birthMonth: toNullableNumber(value("birthMonth")),
    birthDay: toNullableNumber(value("birthDay")),
    importedAge: toNullableNumber(value("importedAge")),
    importedAgeAsOf: nullableString(value("importedAgeAsOf")),
    birthdateSource: value("birthdateSource") as "manual" | "legacy-import" | undefined,
    dateOfBirthUnknown: value("dateOfBirthUnknown") === true,
    reportedAge: toNullableNumber(value("reportedAge")),
    ageAsOfDate: resident.ageAsOfDate || nullableString(value("ageAsOfDate")),
    ageSource: resident.ageSource || (value("ageSource") as AgeSource | null | undefined),
    computedAge: resident.ageYears,
    minimumAgeYears: resident.ageYears,
    maximumAgeYears: resident.ageYears,
    legacyOriginalBirthdate: value("legacyOriginalBirthdate"),
    legacyBirthdateRaw: value("legacyBirthdateRaw"),
    legacyBirthdatePrecision: value("legacyBirthdatePrecision"),
    legacyImportedAgeFallback: value("legacyImportedAgeFallback"),
    legacyBirthYearRaw: value("legacyBirthYearRaw"),
    legacyAge: value("legacyAge"),
    legacyOriginalAge: value("legacyOriginalAge"),
    legacyComputedAge: value("legacyComputedAge"),
  });
  if (resident.reportAge === undefined) return resolved;
  return { ...resolved, ageYears: resident.reportAge, minimumAgeYears: resident.reportAge, maximumAgeYears: resident.reportAge,
    ageDisplay: resident.reportAge === null ? "Unknown" : String(resident.reportAge),
    ageBasis: resident.reportAge === null ? "unknown" : resolved.ageBasis };
}

export async function getRbimSubmissionEditContext(
  _submissionId: string,
  residentId: string,
): Promise<RbimSubmissionEditContext> {
  const web = getActiveWebAdapter();
  const values = web.initialValues;

  if (!values) {
    throw new Error("The online household record could not be loaded for editing.");
  }

  const copy = structuredClone(values);
  const selected =
    copy.members.find((member) => member.residentId === residentId) ??
    copy.members[0];
  const selectedResidentName = selected
    ? [selected.firstName, selected.middleName, selected.lastName]
        .map((part) => String(part ?? "").trim())
        .filter(Boolean)
        .join(" ")
    : "Resident";

  return {
    values: copy,
    selectedResidentName,
    householdCode: copy.household.householdCode,
    familyName:
      selected?.familyName ||
      (copy.household.family ? `Family ${copy.household.family}` : "Family A"),
    initialMemberCount: copy.members.length,
  };
}

function toNullableNumber(value: unknown) {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function nullableString(value: unknown) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text || null;
}
